import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/useAuth'
import { Trees, Home, Heart, Info } from 'lucide-react'

interface SocialProject {
  id: string
  name: string
  description: string | null
  project_type: 'trees' | 'family_help'
  is_active: boolean
}

interface TenantSocialProject {
  id: string
  tenant_id: string
  social_project_id: string
  is_enabled: boolean
  enabled_at: string | null
  social_project: SocialProject
}

export const SocialEngagementSettings = () => {
  const { toast } = useToast()
  const { tenantId } = useAuth()
  const [availableProjects, setAvailableProjects] = useState<SocialProject[]>([])
  const [tenantProjects, setTenantProjects] = useState<TenantSocialProject[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState<string | null>(null)

  useEffect(() => {
    if (tenantId) {
      fetchProjects()
    }
  }, [tenantId])

  const fetchProjects = async () => {
    if (!tenantId) return

    try {
      // Fetch all available social projects
      const { data: projects, error: projectsError } = await supabase
        .from('social_projects')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: true })

      if (projectsError) throw projectsError

      // Fetch tenant's enabled projects
      const { data: tenantSettings, error: tenantError } = await supabase
        .from('tenant_social_projects')
        .select(`
          *,
          social_project:social_projects (*)
        `)
        .eq('tenant_id', tenantId)

      if (tenantError) throw tenantError

      setAvailableProjects(projects as SocialProject[] || [])
      setTenantProjects(tenantSettings as TenantSocialProject[] || [])
    } catch (error) {
      console.error('Error fetching social projects:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Projekte konnten nicht geladen werden.'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleToggleProject = async (projectId: string, enabled: boolean) => {
    if (!tenantId) return

    setUpdating(projectId)

    try {
      console.log('Toggling project:', { projectId, enabled, tenantId })
      
      // Check if entry exists
      const { data: existing, error: checkError } = await supabase
        .from('tenant_social_projects')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('social_project_id', projectId)
        .maybeSingle()

      if (checkError) {
        console.error('Error checking existing project:', checkError)
        throw checkError
      }

      console.log('Existing entry:', existing)

      if (existing) {
        // Update existing entry
        const { error } = await supabase
          .from('tenant_social_projects')
          .update({
            is_enabled: enabled,
            enabled_at: enabled ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('tenant_id', tenantId)
          .eq('social_project_id', projectId)

        if (error) {
          console.error('Error updating project:', error)
          throw error
        }
      } else {
        // Insert new entry
        const { error } = await supabase
          .from('tenant_social_projects')
          .insert({
            tenant_id: tenantId,
            social_project_id: projectId,
            is_enabled: enabled,
            enabled_at: enabled ? new Date().toISOString() : null
          })

        if (error) {
          console.error('Error inserting project:', error)
          throw error
        }
      }

      toast({
        title: enabled ? 'Projekt aktiviert' : 'Projekt deaktiviert',
        description: `Das soziale Projekt wurde ${enabled ? 'für Ihre Kunden aktiviert' : 'deaktiviert'}.`
      })

      // Refresh projects after successful change
      await fetchProjects()
    } catch (error: any) {
      console.error('Error toggling project:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Projekteinstellung konnte nicht geändert werden: ${error?.message || 'Unbekannter Fehler'}`
      })
    } finally {
      setUpdating(null)
    }
  }

  const isProjectEnabled = (projectId: string) => {
    const tenantProject = tenantProjects.find(tp => tp.social_project_id === projectId)
    return tenantProject?.is_enabled || false
  }

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return <Trees className="h-5 w-5 text-green-600" />
      case 'family_help': return <Heart className="h-5 w-5 text-red-600" />
      default: return <Heart className="h-5 w-5 text-primary" />
    }
  }

  const getProjectTypeName = (type: string) => {
    switch (type) {
      case 'trees': return 'Baumpflanzaktion'
      case 'family_help': return 'Familienhilfe'
      default: return type
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Laden...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            Soziales Engagement
          </CardTitle>
          <CardDescription>
            Aktivieren Sie soziale Projekte für Ihre Kunden. Für jede erfolgreiche Empfehlung 
            wird automatisch ein Beitrag zu den aktivierten Projekten geleistet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">So funktioniert's:</p>
                <p>
                  Wenn Sie ein soziales Projekt aktivieren, wird für jeden Kunden, der durch 
                  eine Empfehlung gewonnen wird, automatisch ein Beitrag (z.B. ein Baum) 
                  dem entsprechenden Projekt gutgeschrieben. Ihre Kunden können ihren 
                  persönlichen Beitrag in der "Soziales Engagement" Seite verfolgen.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {availableProjects.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8 text-muted-foreground">
              <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">Keine sozialen Projekte verfügbar</h3>
              <p className="text-sm">
                Derzeit sind keine sozialen Projekte verfügbar. 
                Kontaktieren Sie einen Superadmin, um Projekte zu erstellen.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {availableProjects.map((project) => {
            const enabled = isProjectEnabled(project.id)
            const isUpdating = updating === project.id

            return (
              <Card key={project.id} className={enabled ? 'border-primary/50 bg-primary/5' : ''}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getProjectIcon(project.project_type)}
                      <div>
                        <CardTitle className="text-lg">{project.name}</CardTitle>
                        <Badge variant="outline" className="mt-1">
                          {getProjectTypeName(project.project_type)}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`project-${project.id}`}
                        checked={enabled}
                        disabled={isUpdating}
                        onCheckedChange={(checked) => handleToggleProject(project.id, checked)}
                      />
                      <Label htmlFor={`project-${project.id}`}>
                        {enabled ? 'Aktiv' : 'Inaktiv'}
                      </Label>
                    </div>
                  </div>
                  {project.description && (
                    <CardDescription className="mt-2">
                      {project.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  {enabled && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        <strong>✓ Aktiviert:</strong> Ihre Kunden sehen dieses Projekt und 
                        können ihren Beitrag verfolgen.
                      </p>
                    </div>
                  )}
                  {!enabled && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-sm text-gray-600">
                        Aktivieren Sie dieses Projekt, um es für Ihre Kunden sichtbar zu machen.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}